import { Profile } from 'src/app/general-components/profile/profile';

import { Component, OnInit, AfterViewInit } from '@angular/core';
import { FeedbackType } from 'src/app/general-components/feedback/feedback-type';
import { FeedbackSettings } from 'src/app/general-components/feedback/feedback-settings';
import { AuthService } from 'src/app/auth/auth.service';
import { Router } from '@angular/router';
import { ProductImportState, ProductImportStateForUpdate, ProductImportStateValues } from '../../product-import-types';
import { ProductImportService } from '../../product-import.service';
import { ProductDataState, ProductDataStateForUpdate, ProductDataStateValues } from '../../product-data-state-types';

@Component({
  selector: 'app-product-import-flow-control',
  templateUrl: './product-import-flow-control.component.html',
  styleUrls: ['./product-import-flow-control.component.scss'],
})
export class ProductImportFlowControlComponent implements OnInit, AfterViewInit {
  profile!: Profile;

  currentImportState: ProductImportState | null = null;
  selectedImportState: ProductImportStateValues | '' = '';
  importComment: string = '';
  readonly productImportStateValues = ProductImportStateValues;
  readonly availableImportStates: ProductImportStateValues[] = [
    ProductImportStateValues.MS_IDLE,
    ProductImportStateValues.MS_DATA_READY,
    ProductImportStateValues.MS_RUNNING_IMPORT,
    ProductImportStateValues.MS_ERROR_DURING_IMPORT,
  ];
  isImportStateLoading: boolean = false;
  isImportStateUpdating: boolean = false;

  currentDataState: ProductDataState | null = null;
  selectedDataState: ProductDataStateValues | null = null;
  dataStateComment: string = '';
  readonly productDataStateValues = ProductDataStateValues;
  readonly availableDataStates: ProductDataStateValues[] = [
    ProductDataStateValues.ERROR,
    ProductDataStateValues.IDLE,
    ProductDataStateValues.PROCESSING,
    ProductDataStateValues.REVIEWREQUIRED,
    ProductDataStateValues.READYFOREXPORT,
    ProductDataStateValues.EXPORTING,
    ProductDataStateValues.READYFORCOPYING,
  ];
  isDataStateLoading: boolean = false;
  isDataStateUpdating: boolean = false;

  feedbackSettings: FeedbackSettings = {
    identifier: '',
    show: false,
    type: FeedbackType.error,
    message: 'Er is iets misgegaan sorry',
    closeButton: false,
  };
  hasProductFactoryAccess: boolean = false;
  cognitoGroups: string[] = [];

  private readonly productFactoryGroupName = 'AWS-PF';

  private readonly dataStateLabels: Record<ProductDataStateValues, string> = {
    [ProductDataStateValues.ERROR]: 'Fout',
    [ProductDataStateValues.IDLE]: 'Inactief',
    [ProductDataStateValues.PROCESSING]: 'Verwerken',
    [ProductDataStateValues.REVIEWREQUIRED]: 'Review vereist',
    [ProductDataStateValues.READYFOREXPORT]: 'Gereed voor export',
    [ProductDataStateValues.EXPORTING]: 'Bezig met exporteren',
    [ProductDataStateValues.READYFORCOPYING]: 'Gereed voor kopiëren',
  };

  constructor(
    private service: ProductImportService,
    private authService: AuthService,
    private router: Router
  ) {}

  get isLoading(): boolean {
    return this.isImportStateLoading || this.isDataStateLoading;
  }

  ngOnInit(): void {
    this.cognitoGroups = this.authService.getCognitoGroups();
    this.hasProductFactoryAccess = this.cognitoGroups.includes(this.productFactoryGroupName);

    if (this.hasProductFactoryAccess) {
      this.loadImportState();
      this.loadDataState();
    }
  }

  ngAfterViewInit(): void {
    if (this.hasProductFactoryAccess) {
      this.profile = this.authService.getUserInfo();
    }
  }

  updateFeedback(
    type: FeedbackType,
    message: string,
    closeButton: boolean = true,
    buttonText: string | undefined = undefined
  ): void {
    this.feedbackSettings.identifier = '';
    this.feedbackSettings.show = true;
    this.feedbackSettings.closeButton = closeButton;
    this.feedbackSettings.buttonText = buttonText;
    this.feedbackSettings.type = type;
    this.feedbackSettings.message = message;
  }

  loadImportState(): void {
    this.isImportStateLoading = true;
    this.service.getProductImportState().subscribe({
      next: (state: ProductImportState) => {
        this.currentImportState = state;
        this.selectedImportState = state.state;
        this.isImportStateLoading = false;
      },
      error: (error: Error) => {
        this.updateFeedback(
          FeedbackType.error,
          'Fout opgetreden bij het ophalen van de importstaat',
          false,
          'Terug naar hoofdmenu'
        );
        this.isImportStateLoading = false;
        console.error(error);
      },
    });
  }

  loadDataState(): void {
    this.isDataStateLoading = true;
    this.service.getProductDataState().subscribe({
      next: (state: ProductDataState) => {
        this.currentDataState = state;
        this.selectedDataState = state.state;
        this.dataStateComment = state.comment;
        this.isDataStateLoading = false;
      },
      error: (error: Error) => {
        this.updateFeedback(
          FeedbackType.error,
          'Fout opgetreden bij het ophalen van de data-staat',
          false,
          'Terug naar hoofdmenu'
        );
        this.isDataStateLoading = false;
        console.error(error);
      },
    });
  }

  updateImportState(): void {
    if (!this.selectedImportState) {
      this.updateFeedback(FeedbackType.error, 'Selecteer een importstaat om bij te werken');
      return;
    }

    this.isImportStateUpdating = true;

    const updateData: ProductImportStateForUpdate = {
      state: this.selectedImportState,
      comment: this.importComment || '',
      updatedByHint: this.profile.fullName,
    };

    this.service.updateProductImportState(updateData).subscribe({
      next: (updatedState: ProductImportState) => {
        this.currentImportState = updatedState;
        this.importComment = '';
        this.updateFeedback(FeedbackType.success, 'Importstaat succesvol bijgewerkt');
        this.isImportStateUpdating = false;
      },
      error: (error: Error) => {
        this.updateFeedback(
          FeedbackType.error,
          'Fout opgetreden bij het bijwerken van de importstaat',
          false,
          'Terug naar hoofdmenu'
        );
        this.isImportStateUpdating = false;
        console.error(error);
      },
    });
  }

  updateDataState(): void {
    if (this.selectedDataState == null) {
      this.updateFeedback(FeedbackType.error, 'Selecteer een data-staat om bij te werken');
      return;
    }

    if (this.selectedDataState === ProductDataStateValues.ERROR) {
      this.updateFeedback(FeedbackType.error, 'Data-staat kan niet naar Fout worden gezet');
      return;
    }

    this.isDataStateUpdating = true;

    const updateData: ProductDataStateForUpdate = {
      state: this.selectedDataState,
      comment: this.dataStateComment.trim() || undefined,
    };

    this.service.updateProductDataState(updateData).subscribe({
      next: (updatedState: ProductDataState) => {
        this.currentDataState = updatedState;
        this.selectedDataState = updatedState.state;
        this.dataStateComment = updatedState.comment;
        this.updateFeedback(FeedbackType.success, 'Data-staat succesvol bijgewerkt');
        this.isDataStateUpdating = false;
      },
      error: (error: Error) => {
        this.updateFeedback(
          FeedbackType.error,
          'Fout opgetreden bij het bijwerken van de data-staat',
          false,
          'Terug naar hoofdmenu'
        );
        this.isDataStateUpdating = false;
        console.error(error);
      },
    });
  }

  canUpdateImportState(): boolean {
    if (this.currentImportState == null) {
      return false;
    }
    return (
      this.selectedImportState !== '' &&
      this.selectedImportState !== this.currentImportState.state &&
      !this.isImportStateUpdating
    );
  }

  canUpdateDataState(): boolean {
    if (this.currentDataState == null || this.selectedDataState == null) {
      return false;
    }
    return this.selectedDataState !== this.currentDataState.state && !this.isDataStateUpdating;
  }

  getImportStateDisplayName(state: ProductImportStateValues): string {
    switch (state) {
      case ProductImportStateValues.MS_IDLE:
        return 'Inactief';
      case ProductImportStateValues.MS_DATA_READY:
        return 'Data gereed';
      case ProductImportStateValues.MS_RUNNING_IMPORT:
        return 'Import wordt uitgevoerd';
      case ProductImportStateValues.MS_ERROR_DURING_IMPORT:
        return 'Fout tijdens de import';
      default:
        return state;
    }
  }

  getImportStateBadgeClass(state: ProductImportStateValues): string {
    switch (state) {
      case ProductImportStateValues.MS_IDLE:
        return 'badge bg-secondary';
      case ProductImportStateValues.MS_DATA_READY:
        return 'badge bg-warning text-dark';
      case ProductImportStateValues.MS_RUNNING_IMPORT:
        return 'badge bg-primary';
      case ProductImportStateValues.MS_ERROR_DURING_IMPORT:
        return 'badge bg-danger';
      default:
        return 'badge bg-secondary';
    }
  }

  getDataStateDisplayName(state: ProductDataStateValues | null | undefined): string {
    if (state == null) {
      return 'Onbekende staat';
    }
    return this.dataStateLabels[state];
  }

  getDataStateBadgeClass(state: ProductDataStateValues | null | undefined): string {
    switch (state) {
      case ProductDataStateValues.ERROR:
        return 'badge bg-danger';
      case ProductDataStateValues.IDLE:
        return 'badge bg-secondary';
      case ProductDataStateValues.PROCESSING:
        return 'badge bg-info text-dark';
      case ProductDataStateValues.REVIEWREQUIRED:
        return 'badge bg-warning text-dark';
      case ProductDataStateValues.READYFOREXPORT:
        return 'badge bg-success';
      case ProductDataStateValues.EXPORTING:
        return 'badge bg-primary';
      case ProductDataStateValues.READYFORCOPYING:
        return 'badge bg-success';
      default:
        return 'badge bg-secondary';
    }
  }

  returnHome(): void {
    void this.router.navigate(['home']);
  }
}
